import type { MouseEvent } from "react";
import { IconButton } from "@/components/IconButton";

interface WatchToggleButtonProps {
  watched: boolean;
  updating?: boolean;
  className?: string;
  wrapperClassName?: string;
  cardOverlay?: boolean;
  watchedLabel: string;
  unwatchedLabel: string;
  watchedAccessibleName?: string;
  unwatchedAccessibleName?: string;
  onToggle: () => void;
}

/**
 * Shared two-way watched control.
 *
 * Activation is a native button click. #809 looked like a WebKit input problem
 * for seven revisions; the taps were always arriving and the mutation was being
 * rejected at the edge WAF, so no pointer-level handling is warranted here.
 */
export function WatchToggleButton({
  watched,
  updating = false,
  className,
  wrapperClassName,
  cardOverlay = false,
  watchedLabel,
  unwatchedLabel,
  watchedAccessibleName,
  unwatchedAccessibleName,
  onToggle,
}: WatchToggleButtonProps) {
  const label = watched ? unwatchedLabel : watchedLabel;
  const accessibleName = watched
    ? (unwatchedAccessibleName ?? unwatchedLabel)
    : (watchedAccessibleName ?? watchedLabel);
  const buttonClassName = ["watch-toggle-button", className].filter(Boolean).join(" ");

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    // The control renders inside card surfaces that carry their own activation.
    // Stop the event here so toggling watched state never doubles as navigation.
    event.stopPropagation();
    onToggle();
  }

  return (
    <IconButton
      type="button"
      className={buttonClassName}
      wrapperClassName={wrapperClassName}
      wrapperStyle={cardOverlay ? { position: "absolute", top: 10, right: 10, zIndex: 3 } : undefined}
      label={label}
      accessibleName={accessibleName}
      aria-pressed={watched}
      disabled={updating}
      onClick={handleClick}
    >
      ✓
    </IconButton>
  );
}
